import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from './store';
import { Col, Row, Spin, Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { setSelectedRoute } from './redux/slices/customerSlice';
import { GENERAL } from './utils/constants';
import { hasUserPermission } from './utils/permissionUtils';

type ProtectedRouteProps = {
    route: string;
    children: React.ReactNode;
};

const ProtectedRoute = ({ route, children }: ProtectedRouteProps) => {
    const { customerSites, customerSitesLoading, permittedCustomersLoading, selectedSite, selectedCustomerName, userInfo } = useSelector((state: RootState) => state.userInfo);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        dispatch(setSelectedRoute(route));
    }, [route, dispatch]);

    // Allow access to loki-logging for all logged-in users
    if (route.toLowerCase() === 'logManagement') {
        return <>{children}</>;
    }

    if (customerSites.length > 0 && !isEmpty(selectedSite)) {
        const selectedSiteData = customerSites.find(site => site.name === selectedSite);
        const presentComponents = selectedSiteData?.preferences?.present_components || [];

        if (isEmpty(selectedSiteData)) {
            navigate('/404');
        }

        // Level 1: Check user-level permission FIRST (most restrictive)
        const userHasPermission = hasUserPermission(userInfo, route, selectedCustomerName);
        if (!userHasPermission) {
            return <Navigate to="/403" replace />;
        }

        // Level 3: Check site-level component availability
        if (presentComponents.map(comp => comp.toLowerCase()).includes(route.toLowerCase())) {
            return <>{children}</>;
        }
        else return <Navigate to={`/${selectedCustomerName}/${selectedSite}/${GENERAL}`} replace />;
    }

    if (customerSitesLoading || permittedCustomersLoading || !selectedSite) {
        return (
            <Spin size="large" style={{ backgroundColor: 'transparent' }}>
                <Row justify="center">
                    <Col xs={24} sm={22} md={20} lg={18} xl={16} className="loadingCol">
                        <Typography.Title level={2} type="secondary">
                            Loading. Please wait!
                        </Typography.Title>
                    </Col>
                </Row>
            </Spin>
        );
    }

    // Ensure a return value in all cases
    return <Navigate to="/403" replace />;
};

export default ProtectedRoute;